import { Mixin }           from '$/common/lib/utils';
import { Field, JSONable } from '$/entities/lib/JSONable';

export enum CertnCheckType {
	AdverseMedia                 = 'ADVERSE_MEDIA_1',
	GlobalWatchlistAndSanctions  = 'GLOBAL_WATCHLIST_AND_SANCTIONS_1',
	PoliticallyExposedPersons    = 'POLITICALLY_EXPOSED_PERSONS_1',
}

@Mixin(JSONable)
export class ValueWithSources {

	@Field()
	value: string;

	@Field()
	title?: string;

	@Field(String)
	sources: string[];

	constructor(valueWithSources: ValueWithSources) {
		Object.assign(this, valueWithSources);
	}

	static fromCertn(certnReport: any) {
		return new this({
			value   : certnReport.value,
			title   : certnReport.title,
			sources : certnReport.sources,
		});
	}

}

@Mixin(JSONable)
class AdverseMedia {

	@Field(ValueWithSources)
	names: ValueWithSources[];

	@Field(ValueWithSources)
	urls: ValueWithSources[];

	constructor(adverseMedia: Partial<AdverseMedia>) {
		Object.assign(this, adverseMedia);
	}

	static fromCertn(adverseMedia: any) {
		const results = adverseMedia.supplementary_information.am_search_results;
		return new this({
			names : results.full_name?.map(name => ValueWithSources.fromCertn(name)) ?? [],
			urls  : results.urls?.map(url => ValueWithSources.fromCertn(url)) ?? [],
		});
	}

}

@Mixin(JSONable)
class GlobalWatchlistAndSanctions {

	@Field(ValueWithSources)
	names: ValueWithSources[];

	@Field(ValueWithSources)
	documents: ValueWithSources[];

	@Field(ValueWithSources)
	nationalities: ValueWithSources[];

	constructor(globalWatchlistAndSanctions: Partial<GlobalWatchlistAndSanctions>) {
		Object.assign(this, globalWatchlistAndSanctions);
	}

	static fromCertn(globalWatchlistAndSanctions: any) {
		const results = globalWatchlistAndSanctions.supplementary_information.gws_search_results;
		return new this({
			names         : results.full_name?.map(name => ValueWithSources.fromCertn(name)) ?? [],
			documents     : results.documents?.map(document => ValueWithSources.fromCertn(document)) ?? [],
			nationalities : results.nationality?.map(nationality => ValueWithSources.fromCertn(nationality)) ?? [],
		});
	}

}

@Mixin(JSONable)
class PoliticallyExposedPersons {

	@Field()
	pepLevels: number[];

	@Field(ValueWithSources)
	names: ValueWithSources[];

	@Field(ValueWithSources)
	aliases: ValueWithSources[];

	@Field(ValueWithSources)
	nationalities: ValueWithSources[];

	@Field(ValueWithSources)
	datesOfBirth: ValueWithSources[];

	constructor(politicallyExposedPersons: Partial<PoliticallyExposedPersons>) {
		Object.assign(this, politicallyExposedPersons);
	}

	static fromCertn(politicallyExposedPersons: any) {
		const results = politicallyExposedPersons.supplementary_information.pep_search_results;

		let pepLevels: number[] = [];
		if (_.isNotEmpty(results)) {
			const allResults = [ ...results.full_name, ...results.aliases, ...results.nationality, ...results.date_of_birth ];
			pepLevels        = _.uniq(allResults.map(result => result.pep_level)).sort();
		}

		return new this({
			pepLevels,
			names         : results.full_name?.map(name => ValueWithSources.fromCertn(name)) ?? [],
			aliases       : results.aliases?.map(alias => ValueWithSources.fromCertn(alias)) ?? [],
			nationalities : results.nationality?.map(nationality => ValueWithSources.fromCertn(nationality)) ?? [],
			datesOfBirth  : results.date_of_birth?.map(dateOfBirth => ValueWithSources.fromCertn(dateOfBirth)) ?? [],
		});
	}

}

@Mixin(JSONable)
export class BackgroundCheckData {

	@Field()
	error?: string;

	@Field()
	adverseMedia: AdverseMedia;

	@Field()
	globalWatchlistAndSanctions: GlobalWatchlistAndSanctions;

	@Field()
	politicallyExposedPersons: PoliticallyExposedPersons;

	constructor(error?: string) {
		this.error = error;
	}

	static fromCertn(certnReport: any) {
		const report = new this();

		const checks                      = certnReport.checks;
		const adverseMedia                = checks.find(check => check.type === CertnCheckType.AdverseMedia);
		const globalWatchlistAndSanctions = checks.find(check => check.type === CertnCheckType.GlobalWatchlistAndSanctions);
		const politicallyExposedPersons   = checks.find(check => check.type === CertnCheckType.PoliticallyExposedPersons);

		report.adverseMedia                = AdverseMedia.fromCertn(adverseMedia);
		report.globalWatchlistAndSanctions = GlobalWatchlistAndSanctions.fromCertn(globalWatchlistAndSanctions);
		report.politicallyExposedPersons   = PoliticallyExposedPersons.fromCertn(politicallyExposedPersons);

		return report;
	}

	static getSample() {
		return {
			adverseMedia : {
				urls : [
					{
						title   : 'Who should lead the Mounties? The RCMP union has some ideas',
						value   : 'https://www.thestar.com/news/canada/2023/04/26/who-should-lead-the-mounties-the-rcmp-union-has-some-ideas.html',
						sources : [
							'Thestar - Vancouver',
						],
					},
					{
						title   : 'Glen Argan: Admission of systemic racism only a start',
						value   : 'https://www.catholicregister.org/opinion/columnists/item/31743-glen-argan-admission-of-systemic-racism-only-a-start',
						sources : [
							'The Catholic Register - Opinion',
						],
					},
					{
						title   : 'RCMP commissioner calls for patience as force grapples with workplace harassment',
						value   : 'http://easternontarionetwork.com/2018/07/26/rcmp-commissioner-calls-for-patience-as-force-grapples-with-workplace-harassment/',
						sources : [
							'Easternontarionetwork.com - CANADA & THE WORLD',
						],
					},
					{
						title   : "Mountie has ‘impression' Liberal government interfered with N.S. mass shooting probe",
						value   : 'https://www.todayville.com/mountie-has-impression-liberal-government-interfered-with-n-s-mass-shooting-probe/',
						sources : [
							'Todayville',
						],
					},
					{
						title   : "Morning Update: Senior civilian Mountie rebukes RCMP Commissioner John Doe for ‘belittling' behaviour",
						value   : 'https://www.theglobeandmail.com/canada/article-morning-update-senior-civilian-mountie-rebukes-rcmp-commissioner/',
						sources : [
							'The Globe and Mail',
						],
					},
				],
				names : [
					{
						value   : 'John Doe',
						sources : [
							'Easternontarionetwork.com - CANADA & THE WORLD',
							'The Catholic Register - Opinion',
							'The Globe and Mail',
							'Thestar - Vancouver',
							'Todayville',
						],
					},
				],
			},
			politicallyExposedPersons : {
				names : [
					{
						value   : 'John Doe',
						sources : [
							'PEPChecker',
							'cbc.ca',
							'ctvnews.ca',
							'globalnews.ca',
							'nationalpost.com',
							'www.rcmp-grc.gc.ca',
						],
					},
				],
				aliases : [
					{
						value   : 'John C. Doe',
						sources : [
							'PEPChecker',
						],
					},
					{
						value   : 'John A.  Doe',
						sources : [
							'LittleSis',
						],
					},
					{
						value   : 'John B.  Doe',
						sources : [
							'LittleSis',
						],
					},
				],
				pepLevels : [
					4,
				],
				datesOfBirth : [
					{
						value   : '1965-12-31',
						sources : [
							'PEPChecker',
						],
					},
				],
				nationalities : [
					{
						value   : 'Canada',
						sources : [
							'PEPChecker',
						],
					},
				],
			},
			globalWatchlistAndSanctions : {
				names         : [],
				documents     : [],
				nationalities : [],
			},
		};
	}

}

